/**
 * The live room, before the host goes live and while they are, as the confirmed prototype draws it (g-front, "the
 * minutes before" and "the live room"): the top line, the stage (a countdown and tonight's agenda, then the host's
 * big tile with a corner tile, a shared screen, or everyone at once), the quick reactions always on screen, the dock,
 * the row of faces with your own seat, and the column of Room, People, Hands and the host's private chat.
 *
 * Everything that counts is today's: the meeting (`Meeting`, RealtimeKit), the room's socket (chat, presence,
 * reactions, who may share a screen), the hands, the host's call-on and spotlight, small rooms and reconnecting.
 */
import {
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ChatToRoom, PresenceFace, ShareRule } from "../../../shared/live-protocol";
import { OFFER_FOLD_MS } from "../../../shared/offer";
import type {
  HandoutConfig,
  OfferJson,
  PollConfig,
  QuestionConfig,
  ReactionConfig,
  WatchInteraction,
  WatchSession,
} from "../../../shared/types";
import { assetUrl } from "../../lib/api";
import { fmt } from "../../lib/when";
import {
  canChooseSpeaker,
  handsInOrder,
  type Meeting,
  type MeetingState,
  placeInLine,
} from "../../live/meeting/meeting";
import { RoomSound } from "../../live/meeting/RoomSound";
import { namedCameras } from "../../live/meeting/facing";
import { initialsOf, photoOfHost } from "../../live/meeting/table";
import type { RoomChat as RoomChatState } from "../../live/room/chat";
import type { AnswerFn } from "../TalkPlayer";
import type { LiveReactions } from "../Reactions";
import { Brand, Sheet, Toast, useToast } from "../room/chrome";
import { GIcon, type GIconName } from "../room/GIcon";
import { OfferPress, offerPill } from "../room/RoomOffer";
import { useMediaQuery } from "../smallScreen";
import type { WatchThread } from "../WatchPageView";
import type { PublicBrand } from "../../../shared/types";
import { HostAsks, HostSmallRooms, HostTools, type HostKit, useAsks, useChime, useHandsLit } from "./hostParts";
import { type ColumnTab, LiveColumn } from "./LiveColumn";
import { centredRow, type GridFit, gridFit } from "./gridFit";
import { useLineup } from "./lineup";
import { Floats, firstOf, QuickReactions, Seat, TrackVideo, useReactionsFit } from "./liveParts";

const WIDE = "(min-width:1024px)";
const PHONE = "(max-width:719px)";
const GRID_GAP = 4;
/** A room this big shows small round faces in the row, as the kit sends pictures for only a few. */
const BIG_ROOM = 24;
/** From this many people the Everyone grid gives way to the speaker stage and its row of faces. */
const GRID_UP_TO = 9;

/** The view before the person picks one: alone or a crowd the stage, a small group everyone, a shared screen the stage. */
function autoView(people: number, sharing: boolean, phone: boolean): "stage" | "everyone" {
  if (phone || sharing) return "stage";
  return people >= 2 && people <= GRID_UP_TO ? "everyone" : "stage";
}
/** How long the "you're back" line stays after reconnecting. */
const BACK_MS = 1800;
/** How long "just came in" stays. */
const ARRIVE_MS = 4800;
/** How long a moved-to-a-small-room line stays. */
const BANNER_MS = 7000;

interface LiveMoment {
  up: WatchInteraction | null;
  answer: AnswerFn;
  dismiss: (id: string) => void;
}

interface LiveRoomProps {
  /** The minutes before the host goes live; false once they are. */
  wait: boolean;
  brand: PublicBrand;
  session: WatchSession;
  title: string;
  /** Under the title: the session and the host, or what is on now. */
  sub: string;
  hostFirst: string;
  viewer: { first: string; avatar: string | null };
  /** "Maya goes live at 6:00 PM" and the minutes left, before the start. */
  goesLiveAt: string;
  clock: string | null;
  agenda: string[];
  meeting: Meeting;
  state: MeetingState;
  chat: RoomChatState;
  send: (msg: ChatToRoom) => boolean;
  reacts: LiveReactions;
  thread: WatchThread;
  unreadHost: boolean;
  onReadHost: () => void;
  opensAt: string;
  liveAt: number | null;
  /** The host ended the live while this page was in the room. */
  ended: boolean;
  onAfter: () => void;
  onLeave: () => void;
  moment: LiveMoment;
  offer: { offer: OfferJson; due: boolean; email: string; onConvert: () => void } | null;
  yourPage: string;
  /** The host's page: the same room, with what only the host has. */
  host?: HostKit;
}

/** Who has the big tile and who sits in the corner. */
type Tile = "maya" | "you" | "spot" | "partner" | "share" | "myshare";

function bigTile(state: MeetingState, spot: string | null): { big: Tile; pip: Tile | null } {
  const me = state.faces.find((f) => f.mine);
  if (state.inSmallRoom) return { big: "partner", pip: "you" };
  if (me?.pinned) return { big: "you", pip: "maya" };
  if (state.sharingId && state.sharingId === state.mine.id) return { big: "myshare", pip: "maya" };
  if (spot && state.faces.some((f) => f.id === spot)) return { big: "spot", pip: "maya" };
  if (state.sharingId) return { big: "share", pip: "maya" };
  return { big: "maya", pip: null };
}

/** How many faces the row has room for at this width, beside your own seat and "+N more". */
function seatsFor(width: number, wide: boolean, phone: boolean, big: boolean): number {
  const seatW = big ? (wide ? 44 : phone ? 36 : 46) : wide ? 122 : phone ? 58 : 104;
  const youW = big ? (wide ? 94 : phone ? 58 : 104) : seatW;
  const moreW = big ? (phone ? 80 : 132) : phone ? 66 : 130;
  if (big) return phone ? 6 : Math.max(6, Math.min(24, 2 * Math.floor((width - youW - moreW - 24) / 44)));
  return Math.max(2, Math.floor((width - youW - moreW) / seatW));
}

/** The words on the dock's share button, by the host's rule and whether you share now. */
const SHARE_WORDS: Record<ShareRule, string> = { ask: "Ask to share", host: "Only the host", anyone: "Share" };

/** What the browser is asked to share, as the card offers it. */
const PICKS: [string, GIconName][] = [
  ["Entire screen", "grid"],
  ["A window", "stage"],
  ["A browser tab", "link"],
];

function DockButton({
  icon,
  words,
  className,
  onClick,
  label,
  pressed,
  children,
  id,
}: {
  icon: GIconName;
  words: string;
  className?: string;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  label?: string;
  pressed?: boolean;
  children?: ReactNode;
  id?: string;
}): ReactNode {
  return (
    <button className={className ? `db ${className}` : "db"} type="button" id={id} aria-label={label} aria-pressed={pressed} onClick={onClick}>
      <GIcon name={icon} size={22} />
      <span className="db-t">{words}</span>
      {children}
    </button>
  );
}

/** A small card that opens upward from the dock: the device menus, the share card and the reactions. */
function DockMenu({
  anchor,
  room,
  label,
  className,
  children,
}: {
  anchor: HTMLElement | null;
  room: HTMLElement | null;
  label: string;
  className?: string;
  children: ReactNode;
}): ReactNode {
  const box = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el || !room) return;
    const lr = room.getBoundingClientRect();
    const w = el.offsetWidth;
    if (anchor && anchor.offsetParent) {
      const r = anchor.getBoundingClientRect();
      el.style.left = `${Math.max(8, Math.min(lr.width - w - 8, r.left - lr.left + r.width / 2 - w / 2))}px`;
      el.style.top = `${Math.max(8, r.top - lr.top - el.offsetHeight - 10)}px`;
    } else {
      const dock = room.querySelector(".lv-dock")?.getBoundingClientRect();
      el.style.left = `${Math.max(8, (lr.width - w) / 2)}px`;
      el.style.top = `${Math.max(8, (dock?.top ?? lr.bottom) - lr.top - el.offsetHeight - 10)}px`;
    }
  });
  return (
    <div ref={box} className={className ? `dmenu ${className}` : "dmenu"} role="dialog" aria-label={label}>
      {children}
    </div>
  );
}

/** A device list, in the room's own select. */
function DeviceSelect({ label, choices, chosen, onPick }: { label: string; choices: { id: string; label: string }[]; chosen: string; onPick: (id: string) => void }): ReactNode {
  if (choices.length === 0) return null;
  return (
    <label className="sel">
      <span>{label}</span>
      <select value={chosen} onChange={(e) => onPick(e.target.value)}>
        {choices.map((c) => (
          <option key={c.id} value={c.id}>
            {c.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** What the host runs in the room at its minute: a poll, a question, a reaction, a handout. */
function MomentCard({ moment, host, hostAvatar, onAnswer, onClose }: { moment: WatchInteraction; host: string; hostAvatar: string | null; onAnswer: AnswerFn; onClose: () => void }): ReactNode {
  const [text, setText] = useState("");
  const mine = moment.my_response;
  const face = hostAvatar ? <img className="av" src={hostAvatar} alt="" /> : <span className="av av-i">{initialsOf(host)}</span>;
  let ask = "";
  let body: ReactNode = null;
  let thanks: ReactNode = null;
  if (moment.type === "poll") {
    const cfg = moment.config as PollConfig;
    const picked = mine?.kind === "poll" ? mine.option : null;
    ask = cfg.question ?? "";
    body = (
      <div className="lt-opts">
        {(cfg.options ?? []).map((o) => (
          <button key={o.label} type="button" className={picked === o.label ? "on" : undefined} disabled={picked !== null} onClick={() => onAnswer(moment.id, { kind: "poll", option: o.label })}>
            {o.label}
          </button>
        ))}
      </div>
    );
    thanks = picked ? (
      <>
        <GIcon name="check" size={18} /> Thanks. You said {picked}. Only {host} sees it.
      </>
    ) : null;
  } else if (moment.type === "question") {
    const cfg = moment.config as QuestionConfig;
    ask = cfg.question ?? "";
    const sent = mine?.kind === "question" ? mine.text : null;
    body = sent ? null : (
      <form
        className="lt-act"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) onAnswer(moment.id, { kind: "question", text: text.trim() });
        }}
      >
        <textarea className="mo-ta" dir="auto" maxLength={200} rows={2} aria-label="Your answer" value={text} onChange={(e) => setText(e.target.value)} />
        <button className="btn btn-p" type="submit" disabled={!text.trim()}>
          Send to {host}
        </button>
      </form>
    );
    thanks = sent ? (
      <>
        <GIcon name="check" size={18} /> Sent. Only <bdi>{host}</bdi> sees it.
      </>
    ) : null;
  } else if (moment.type === "reaction") {
    const cfg = moment.config as ReactionConfig;
    ask = cfg.prompt ?? "";
    const felt = mine?.kind === "reaction" ? mine.emoji : null;
    body = (
      <div className="lt-opts">
        {(cfg.reactions ?? []).map((r) => (
          <button key={r.emoji} type="button" className={felt === r.emoji ? "on" : undefined} disabled={felt !== null} onClick={() => onAnswer(moment.id, { kind: "reaction", emoji: r.emoji })}>
            {r.emoji}
          </button>
        ))}
      </div>
    );
    thanks = felt ? (
      <>
        <GIcon name="check" size={18} /> <bdi>{host}</bdi> will see you felt that.
      </>
    ) : null;
  } else {
    const cfg = moment.config as HandoutConfig;
    const saved = mine?.kind === "handout";
    body = (
      <div className="file">
        <span className="file-ic">PDF</span>
        <div>
          <b dir="auto">{cfg.title}</b>
          <span>{cfg.size ? `PDF, ${cfg.size}` : "PDF"}</span>
        </div>
        <a
          className={saved ? "btn done" : "btn btn-p"}
          href={cfg.key ? assetUrl(cfg.key) : undefined}
          download={cfg.file ?? true}
          onClick={() => {
            if (!saved) onAnswer(moment.id, { kind: "handout", downloaded: true });
          }}
        >
          <GIcon name={saved ? "check" : "down"} size={16} /> {saved ? "Saved" : "Download"}
        </a>
      </div>
    );
  }
  const kind = moment.type === "handout" ? `${host} shared a handout` : `${host} asks the room`;
  return (
    <div className="lt" role="dialog" aria-label={kind}>
      <button className="ibtn lt-x" type="button" aria-label="Close" onClick={onClose}>
        <GIcon name="close" size={18} />
      </button>
      <p className="lt-k">
        {face}
        {kind}
      </p>
      {ask ? <h3>{ask}</h3> : null}
      {body}
      {thanks ? <p className="lt-ok">{thanks}</p> : moment.type === "handout" ? null : <p className="lt-ok" style={{ marginTop: 10, color: "var(--ink-3)" }}>Only <bdi>{host}</bdi> sees your answer.</p>}
    </div>
  );
}

/** A small room's grid is laid out in half columns so its short last row can be centred; a big one in whole tiles. */
function gridStyle(fit: GridFit): CSSProperties {
  const cols = fit.hostBig ? `repeat(${fit.cols},${fit.tileWidth}px)` : `repeat(${fit.cols * 2},${(fit.tileWidth - GRID_GAP) / 2}px)`;
  return { gridTemplateColumns: cols, gridAutoRows: `${fit.tileHeight}px` };
}

export function LiveRoom(p: LiveRoomProps): ReactNode {
  const { meeting, state, chat, session, wait } = p;
  const wide = useMediaQuery(WIDE);
  const phone = useMediaQuery(PHONE);
  const room = useRef<HTMLDivElement>(null);
  const { toast, say } = useToast();

  const me = state.faces.find((f) => f.mine) ?? null;
  // The person actually hosting: whoever joined on the host preset, with a camera first. The workspace's
  // host profile (session.host_name, its photo) only stands in until somebody has joined.
  const hosts = state.faces.filter((f) => f.host && !f.mine);
  const hk = p.host;
  // Why me for the host: they are the person at the front of their own room.
  const host = hk ? me : (hosts.find((f) => f.cameraOn) ?? hosts[0] ?? null);
  const hostName = host?.name ?? session.host_name;
  const hostFirst = host ? firstOf(host.name) : p.hostFirst;
  const hostPhoto = host ? photoOfHost(host.name, session.host_name, session.host_avatar_url) : session.host_avatar_url;
  const reconnecting = state.reconnecting;
  const myPlace = me ? placeInLine(state.faces, me.who) : 0;

  // ── the column: beside the stage on a laptop, a sheet on a phone, always there in between ──
  const [colOpen, setColOpen] = useState(wide);
  const [tab, setTab] = useState<ColumnTab>("room");
  const [find, setFind] = useState("");
  const showsCol = wide ? colOpen : phone ? colOpen : true;
  const openCol = useCallback(
    (to?: ColumnTab) => {
      setColOpen(true);
      if (to) setTab(to);
    },
    [],
  );
  useEffect(() => {
    if (tab === "maya" && showsCol) p.onReadHost();
  }, [tab, showsCol, p.onReadHost]);

  // ── the room's chat while the column is away: a count, and a peek at the newest line ──
  const [readLines, setReadLines] = useState(chat.lines.length);
  const reading = showsCol && tab === "room";
  useEffect(() => {
    if (reading) setReadLines(chat.lines.length);
  }, [reading, chat.lines.length]);
  // Why from now on: the lines the room hands a new socket were said before this person came in.
  const [cameAt] = useState(() => Math.floor(Date.now() / 1000) - 1);
  const fresh = chat.lines.slice(readLines).filter((l) => !l.mine && l.at >= cameAt);
  const unreadRoom = reading ? 0 : fresh.length;
  const peek = !showsCol ? (fresh.at(-1) ?? null) : null;
  const peekPhoto = peek ? (peek.host ? photoOfHost(peek.name, session.host_name, session.host_avatar_url) : peek.photo) : null;

  // ── the stage ──
  // Why null until picked: on a computer or tablet the view follows the head count, and a press of the person's own sticks for the visit.
  const [chosen, setView] = useState<"stage" | "everyone" | null>(null);
  const [page, setPage] = useState(0);
  const [spot, setSpot] = useState<string | null>(null);
  const { big: bigT, pip } = bigTile(state, spot);
  const view = chosen ?? autoView(state.faces.length, !!state.sharingId, phone);
  const everyone = view === "everyone" && !state.inSmallRoom && !wait;
  const bigRoom = state.faces.length > BIG_ROOM;
  const sharer = state.sharingId ? state.faces.find((f) => f.id === state.sharingId) ?? null : null;

  // A screen that starts sharing takes the big tile, once.
  const lastShare = useRef(state.sharingId);
  useEffect(() => {
    if (state.sharingId && state.sharingId !== lastShare.current) {
      setSpot(null);
      setView((v) => v && "stage");
    }
    lastShare.current = state.sharingId;
  }, [state.sharingId]);

  // ── called on: your face at the front, and the plain words that say so ──
  const onstage = !!me?.pinned;
  const [lower, setLower] = useState<"call" | null>(null);
  const wasOn = useRef(onstage);
  useEffect(() => {
    if (onstage && !wasOn.current) {
      setLower("call");
      setSpot(null);
      setView("stage");
    }
    if (!onstage && wasOn.current) setLower(null);
    wasOn.current = onstage;
  }, [onstage]);

  // ── small rooms: the host moves you, and you come back ──
  const [banner, setBanner] = useState<ReactNode>(null);
  const bannerTimer = useRef(0);
  const showBanner = useCallback((words: ReactNode, ms = BANNER_MS) => {
    window.clearTimeout(bannerTimer.current);
    setBanner(words);
    bannerTimer.current = window.setTimeout(() => setBanner(null), ms);
  }, []);
  useEffect(() => () => window.clearTimeout(bannerTimer.current), []);
  const wasSmall = useRef(state.inSmallRoom);
  useEffect(() => {
    if (state.inSmallRoom && !wasSmall.current) {
      const others = state.faces.filter((f) => !f.mine).map((f) => f.name);
      showBanner(
        <>
          <GIcon name="people" size={18} />
          <span>
            <b>
              {hostFirst} moved you to a small room{others.length ? ` with ${others.slice(0, 3).join(", ")}` : ""}.
            </b>{" "}
            Say hello. You can go back to the main room any time.
          </span>
        </>,
      );
    }
    if (!state.inSmallRoom && wasSmall.current) {
      showBanner(
        <>
          <GIcon name="check" size={18} />
          <span>You’re back in the main room.</span>
        </>,
        3600,
      );
    }
    wasSmall.current = state.inSmallRoom;
  }, [state.inSmallRoom, state.faces, hostFirst, showBanner]);

  // ── reconnecting: the room stays, your seat turns, and a line says when you are back ──
  const [back, setBack] = useState(false);
  const wasReco = useRef(reconnecting);
  useEffect(() => {
    if (!reconnecting && wasReco.current) {
      setBack(true);
      const t = window.setTimeout(() => setBack(false), BACK_MS);
      wasReco.current = reconnecting;
      return () => window.clearTimeout(t);
    }
    wasReco.current = reconnecting;
  }, [reconnecting]);

  // ── who just came in, before the live ──
  const [arrived, setArrived] = useState<PresenceFace | null>(null);
  const seenFaces = useRef(new Set(chat.faces.map((f) => f.key)));
  // Why a first hearing: the room's first word lists everyone already here, who did not just come in.
  const heardRoom = useRef(chat.here !== null);
  useEffect(() => {
    const fresh = chat.faces.filter((f) => !seenFaces.current.has(f.key) && f.name !== "" && f.key !== chat.you);
    for (const f of chat.faces) seenFaces.current.add(f.key);
    const first = !heardRoom.current;
    heardRoom.current = chat.here !== null;
    if (!wait || first || fresh.length === 0) return;
    const last = fresh[fresh.length - 1];
    setArrived(last);
    const t = window.setTimeout(() => setArrived(null), ARRIVE_MS);
    return () => window.clearTimeout(t);
  }, [chat.faces, wait]);

  // ── the menus over the dock ──
  const [menu, setMenu] = useState<"mic" | "cam" | "share" | "react" | null>(null);
  const anchors = useRef<Record<string, HTMLElement | null>>({});
  const [more, setMore] = useState(false);
  useEffect(() => {
    if (!menu) return;
    const away = (e: PointerEvent) => {
      const t = e.target as HTMLElement;
      if (t.isConnected && !t.closest(".dmenu, [data-menu]")) setMenu(null);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (menu === "react") anchors.current.react?.focus();
      setMenu(null);
    };
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [menu]);

  // ── the dock's line: the seven reactions when they fit, else one React button ──
  const dock = useRef<HTMLDivElement>(null);
  const dockGroup = useRef<HTMLDivElement>(null);
  const reactsFit = useReactionsFit(dock, dockGroup, phone, wide, [wait, state.inSmallRoom]);
  const reactsOff = reconnecting || state.inSmallRoom;
  useEffect(() => {
    if (reactsFit || reactsOff) setMenu((m) => (m === "react" ? null : m));
  }, [reactsFit, reactsOff]);

  // ── sharing a screen: the host's rule, your ask, and your turn ──
  const share = chat.share;
  const [asked, setAsked] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const sharing = state.mine.sharing;
  // Why this tab only: in a second tab of the same person the turn is theirs too, and only the one that asked shares.
  const allowed = asked && share.mine === "allowed";
  useEffect(() => {
    if (!hk && sharing && share.mine !== "allowed") {
      void meeting.toggleScreen();
      say("You stopped sharing.", "share");
    }
  }, [sharing, share.mine, meeting, say, hk]);
  const startShare = (what: string) => {
    setPicked(what);
    setMenu(null);
    meeting.toggleScreen().catch(() => {
      p.send({ type: "share-drop" });
      setAsked(false);
    });
  };
  const stopShare = () => {
    if (sharing) void meeting.toggleScreen();
    if (!hk) p.send({ type: "share-drop" });
    setAsked(false);
    setPicked(null);
    say("You stopped sharing.", "share");
  };
  // Under Anyone the pick asks and shares in one go, as soon as the room says the turn is yours.
  const pendingPick = useRef<string | null>(null);
  useEffect(() => {
    if (allowed && pendingPick.current && !sharing) {
      const what = pendingPick.current;
      pendingPick.current = null;
      startShare(what);
    }
  });
  // Why the browser too: an iPad is wider than a phone and still cannot share a screen.
  const cannotShare = phone || typeof navigator.mediaDevices?.getDisplayMedia !== "function";
  const askShare = () => {
    if (cannotShare) {
      say("Join from a computer to share it.", "share");
      return;
    }
    if (sharing) {
      stopShare();
      return;
    }
    if (hk) {
      void meeting.toggleScreen();
      return;
    }
    setMenu((m) => (m === "share" ? null : "share"));
  };

  // ── the host: who is asking, the tools, small rooms ──
  const raised = useMemo(() => handsInOrder(state.faces).filter((f) => !f.mine), [state.faces]);
  const asks = useAsks(hk ? chat.share.asks : [], hk ? raised : []);
  const playChime = useChime(hk?.chime.on ?? false);
  const askCount = chat.share.asks.length + raised.length;
  const handsLit = useHandsLit(hk ? askCount : 0, showsCol && tab === "hands");
  const [tools, setTools] = useState<"tools" | "rooms" | null>(null);
  const seeAsks = () => openCol("hands");

  // ── hands ──
  const raise = () => {
    if (wait) {
      say(`Hands open when ${hostFirst} goes live.`, "hand");
      return;
    }
    if (onstage) return;
    if (myPlace) {
      void meeting.lowerHand();
      say("Your hand is down.", "hand");
      return;
    }
    void meeting.raiseHand();
  };

  // ── the live's moments and the offer, in the lower third ──
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerFolded, setOfferFolded] = useState(false);
  const offerTimer = useRef(0);
  const due = p.offer?.due ?? false;
  useEffect(() => {
    if (!due || offerFolded || offerOpen) return;
    setOfferOpen(true);
    offerTimer.current = window.setTimeout(() => {
      setOfferOpen(false);
      setOfferFolded(true);
    }, OFFER_FOLD_MS.live);
  }, [due, offerFolded, offerOpen]);
  useEffect(() => () => window.clearTimeout(offerTimer.current), []);
  const foldOffer = () => {
    window.clearTimeout(offerTimer.current);
    setOfferOpen(false);
    setOfferFolded(true);
  };

  // ── the row: your own seat, the faces that matter now, and "+N more" ──
  const rowBox = useRef<HTMLDivElement>(null);
  const [rowWidth, setRowWidth] = useState(1200);
  useLayoutEffect(() => {
    const el = rowBox.current;
    if (!el) return;
    const read = () => {
      const pad = wide ? 48 : 2 * Number.parseFloat(getComputedStyle(el).paddingLeft || "20");
      setRowWidth(el.clientWidth - pad);
    };
    read();
    const watch = new ResizeObserver(read);
    watch.observe(el);
    return () => watch.disconnect();
  }, [wide]);
  const seats = seatsFor(rowWidth, wide, phone, bigRoom);
  // Why stable: a seat that is handed a new function each render is drawn again for every chat line and reaction.
  const bringForward = useCallback((id?: string) => {
    if (!id) return;
    setSpot(id);
    setView("stage");
  }, []);
  const spotInRow = useCallback((id?: string) => id && setSpot(id), []);
  const sayYou = useCallback(() => say(state.mine.cameraOn ? "That’s you. Your camera is on." : "That’s you. Your camera is off.", "cam"), [say, state.mine.cameraOn]);
  const seated = useLineup(state.faces, state.speakingId, seats, bigT === "spot" ? spot : null);
  const others = wait ? [] : seated;
  // The room's presence names this page's own person too: they sit in their own seat, never twice.
  const named = wait ? chat.faces.filter((f) => f.key !== chat.you) : [];
  const waiting = named.slice(-seats);
  // Why photos only: initials stacked that small hide each other; the prototype's "+N more" shows faces.
  const unseated = named.slice(0, named.length - waiting.length).filter((f) => f.photo).slice(-3);
  const total = wait ? (chat.here ?? 0) : state.faces.length;
  const moreN = wait ? Math.max(0, total - waiting.length - 1) : Math.max(0, total - others.length - (host ? 1 : 0) - (me && me !== host ? 1 : 0));
  // Why memoised: asking the meeting for a picture is a draw, and a chat line or reaction must draw no face.
  const youTrack = useMemo(() => meeting.previewTrack() ?? (me ? meeting.trackOf(me.id, "video") : null), [meeting, me, state.mine.cameraOn]);

  // ── the everyone view: the host at double size, a page at a time ──
  const grid = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  // Why only the box: the fit is worked out again when the room for tiles changes or people come and go, never per chat line.
  useLayoutEffect(() => {
    const el = grid.current;
    if (!el) return;
    const read = () => {
      const style = getComputedStyle(el);
      const width = el.clientWidth - Number.parseFloat(style.paddingLeft) - Number.parseFloat(style.paddingRight);
      const height = el.clientHeight - Number.parseFloat(style.paddingTop) - Number.parseFloat(style.paddingBottom);
      setBox((b) => (Math.abs(b.width - width) < 0.5 && Math.abs(b.height - height) < 0.5 ? b : { width, height }));
    };
    read();
    const watch = new ResizeObserver(read);
    watch.observe(el);
    return () => watch.disconnect();
  }, [everyone]);
  const gridPeople = state.faces.filter((f) => !f.host);
  const fit = useMemo(
    () => (box.width > 0 && box.height > 0 ? gridFit({ ...box, people: gridPeople.length + 1, gap: GRID_GAP, kind: phone ? "phone" : wide ? "laptop" : "tablet" }) : null),
    [box, gridPeople.length, phone, wide],
  );
  const short = fit ? centredRow(fit, gridPeople.length + 1) : null;
  const perPage = fit?.perPage ?? 20;
  const pages = Math.max(1, Math.ceil(gridPeople.length / perPage));
  const at = Math.min(page, pages - 1);
  const onGrid = gridPeople.slice(at * perPage, (at + 1) * perPage);

  const spotFace = spot ? state.faces.find((f) => f.id === spot) ?? null : null;
  const partner = state.inSmallRoom ? state.faces.find((f) => !f.mine) ?? null : null;
  const tileClass = (t: Tile) => ["cam", bigT === t ? "big" : "", pip === t ? "pip" : ""].filter(Boolean).join(" ");
  const showTile = (t: Tile) => bigT === t || pip === t;
  const bars = (
    <span className="bars">
      <i />
      <i />
      <i />
    </span>
  );
  const hostPicture = useMemo(
    () =>
      host?.cameraOn ? (
        <TrackVideo track={meeting.trackOf(host.id, "video")} label={host.name} meeting={meeting} peer={host.id} />
      ) : hostPhoto ? (
        <img src={hostPhoto} alt={hostName} />
      ) : (
        <span className="ini big-ini">{initialsOf(hostName)}</span>
      ),
    [host, meeting, hostPhoto, hostName],
  );

  // Why: a row with nobody in it is an empty band under the dock; the phone keeps its row as it was.
  const noRow = !phone && !state.inSmallRoom && (onstage || hk) && (wait ? waiting.length === 0 : others.length === 0) && moreN === 0;
  const lvClass = [
    "lv",
    bigRoom && !wait ? "big" : "",
    wait ? "wait" : "",
    wide && !colOpen ? "col-off" : "",
    phone && colOpen ? "col-on" : "",
    everyone ? "everyone" : "",
    noRow ? "norow" : "",
    bigT === "share" || bigT === "myshare" ? "sharing" : "",
    state.inSmallRoom ? "small" : "",
    reconnecting ? "reco" : "",
    state.mine.softened ? "soft" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const badge = wait ? (
    <span className="lv-badge soon">
      <span className="live-dot" />
      <span>{fmt(chat.here ?? 0)} here</span>
    </span>
  ) : state.inSmallRoom ? (
    <span className="lv-badge">
      <GIcon name="people" size={15} />
      <span>Small room · {fmt(state.faces.length)} here</span>
    </span>
  ) : (
    <span className="lv-badge">
      <span className="live-dot" />
      <span>
        <span className="bl-w">Live now · </span>
        <span className="bl-p">Live · </span>
        {hk ? `${fmt(chat.here ?? state.faces.length)} in the room` : `${fmt(state.faces.length)} here`}
      </span>
    </span>
  );

  const backShare = !!state.sharingId && (spot !== null || everyone) && !onstage;
  const stageCtl = wait ? null : state.inSmallRoom ? (
    <button className="sc-b accent" type="button" onClick={() => void meeting.backToMainRoom()}>
      <GIcon name="people" size={16} /> Back to the main room
    </button>
  ) : (
    <>
      {sharing && !onstage ? (
        <span className="share-bar" role="status">
          <span className="live-dot" style={{ background: "#fff" }} />
          You’re sharing {(picked ?? "your screen").toLowerCase()}
          <button className="btn btn-l" type="button" onClick={stopShare}>
            Stop sharing
          </button>
        </span>
      ) : null}
      {!hk && asked && !sharing && share.mine === "asking" ? (
        <span className="share-wait" role="status">
          <span className="spin" />
          Waiting for {hostFirst} to say yes
          <button
            className="btn btn-l"
            type="button"
            onClick={() => {
              p.send({ type: "share-drop" });
              setAsked(false);
            }}
          >
            Cancel
          </button>
        </span>
      ) : null}
      {!hk && asked && !sharing && share.mine === "allowed" ? (
        <span className="share-wait yes" role="status">
          <GIcon name="check" size={16} />
          {hostFirst} said yes
          <button className="btn btn-l" type="button" onClick={() => setMenu("share")}>
            Pick what to share
          </button>
        </span>
      ) : null}
      {!hk && asked && share.mine === "declined" ? (
        <span className="share-wait" role="status">
          {hostFirst} said not now. You can ask again later.
          <button className="btn btn-l" type="button" onClick={() => setAsked(false)}>
            OK
          </button>
        </span>
      ) : null}
      {everyone && pages > 1 ? (
        <span className="sc-pg">
          <button className="ibtn" type="button" aria-label="Previous page" disabled={at === 0} onClick={() => setPage(at - 1)}>
            <GIcon name="chl" size={18} />
          </button>
          <span>
            Page {at + 1} of {pages}
          </span>
          <button className="ibtn" type="button" aria-label="Next page" disabled={at >= pages - 1} onClick={() => setPage(at + 1)}>
            <GIcon name="chr" size={18} />
          </button>
        </span>
      ) : null}
      {state.sharingId && !sharing && !spot && !onstage && !everyone ? (
        <span className="sc-b tag">
          <GIcon name="share" size={15} /> <bdi>{firstOf(sharer?.name ?? "")}</bdi>’s screen
        </span>
      ) : null}
      {backShare ? (
        <button
          className="sc-b accent"
          type="button"
          onClick={() => {
            setSpot(null);
            setView("stage");
          }}
        >
          <GIcon name="share" size={16} /> Back to the shared screen
        </button>
      ) : null}
      {spot && !backShare ? (
        <button className="sc-b" type="button" onClick={() => setSpot(null)}>
          <GIcon name="chl" size={16} /> Back to <bdi>{hostFirst}</bdi>
        </button>
      ) : null}
      <button
        className="sc-b"
        type="button"
        onClick={() => {
          setView(everyone ? "stage" : "everyone");
          setPage(0);
          setSpot(null);
        }}
      >
        <GIcon name={everyone ? "stage" : "grid"} size={16} /> {everyone ? "Speaker view" : "Show everyone"}
      </button>
      <FullScreenButton room={room} />
    </>
  );

  const lowerCard =
    lower === "call" && onstage ? (
      state.mine.micOn ? (
        <div className="lt call">
          <p className="lt-k">
            <GIcon name="mic" size={16} /> You’re on stage
          </p>
          <h3><bdi>{hostFirst}</bdi> can hear you. Go ahead.</h3>
          <p>When you’re done, <bdi>{hostFirst}</bdi> will bring you back, or you can go back yourself.</p>
          <div className="lt-act">
            <button className="btn btn-l" type="button" onClick={() => void meeting.toggleMic()}>
              <GIcon name="micoff" size={18} /> Mute
            </button>
            <button className="btn btn-g" type="button" onClick={() => void meeting.backToSeat().then(() => say("You’re back in your seat, mic off."))}>
              Back to my seat
            </button>
          </div>
        </div>
      ) : (
        <div className="lt call" role="alertdialog" aria-label={`${hostFirst} called on you`}>
          <p className="lt-k">
            <GIcon name="hand" size={16} /> Your turn
          </p>
          <h3><bdi>{hostFirst}</bdi> called on you. Unmute to speak</h3>
          <p>Your face has everyone’s big tile now. They’ll hear you once you unmute.</p>
          <div className="lt-act">
            <button className="btn btn-l" type="button" onClick={() => void meeting.toggleMic()}>
              <GIcon name="mic" size={18} /> Unmute
            </button>
            <button className="btn btn-g" type="button" onClick={() => void meeting.backToSeat().then(() => say("You’re back in your seat, mic off."))}>
              Back to my seat
            </button>
          </div>
        </div>
      )
    ) : p.moment.up && !wait ? (
      <MomentCard moment={p.moment.up} host={hostFirst} hostAvatar={hostPhoto} onAnswer={p.moment.answer} onClose={() => p.moment.dismiss(p.moment.up?.id ?? "")} />
    ) : offerOpen && p.offer && !wait ? (
      <div className="lt" role="dialog" aria-label={`${hostFirst}’s offer`}>
        <button className="ibtn lt-x" type="button" aria-label="Fold it away" onClick={foldOffer}>
          <GIcon name="close" size={18} />
        </button>
        <p className="lt-k">
          {hostPhoto ? <img className="av" src={hostPhoto} alt="" /> : null}
          {hostFirst}’s offer
        </p>
        {p.offer.offer.text ? <h3 dir="auto">{p.offer.offer.text}</h3> : null}
        <div className="lt-act">
          <OfferPress offer={p.offer.offer} host={hostFirst} viewerEmail={p.offer.email} className="btn btn-p" onConvert={p.offer.onConvert} onChat={() => openCol("maya")}>
            {offerPill(p.offer.offer, hostFirst)} <GIcon name="arrowr" size={18} />
          </OfferPress>
          <button className="btn btn-q" type="button" onClick={foldOffer}>
            Later
          </button>
        </div>
      </div>
    ) : null;

  const tuckCol = () => setColOpen(false);
  return (
    <div className={lvClass} ref={room}>
      <RoomSound faces={state.faces} soundsOf={(id) => meeting.soundsOf(id)} speakerId={state.speakerId} />
      <div className="lv-main">
        <header className="lv-top">
          {hk ? (
            <a className="lv-back" href={hk.backTo}>
              <GIcon name="chl" size={16} /> Back
            </a>
          ) : null}
          <Brand brand={p.brand} href={p.yourPage} />
          <span className="lv-tt" style={{ marginLeft: 6 }}>
            <b dir="auto">{p.title}</b>
            <span dir="auto">{p.sub}</span>
          </span>
          {hk ? (
            <span className="hk-meta">
              {state.recording === "on" ? (
                <span className="hk-rec">
                  <i aria-hidden="true" />
                  <span className="hk-recw">Recording </span>
                  <span className="hk-clock">{hk.recordingSec}</span>
                </span>
              ) : state.recording === "paused" ? (
                <span className="hk-rec paused">
                  <i aria-hidden="true" />
                  Recording paused
                </span>
              ) : (
                <span className="hk-rec off">
                  Live <span className="hk-clock">{hk.recordingSec}</span>
                </span>
              )}
              {hk.shareUrl ? (
                <button className="hk-copy" type="button" onClick={hk.onCopy}>
                  <GIcon name="copy" size={15} /> Copy link
                </button>
              ) : null}
              {hk.mood ? <span className="hk-mood">{hk.mood}</span> : null}
            </span>
          ) : null}
          {badge}
          <button className="lv-more" type="button" aria-label="More" onClick={() => setMore(true)}>
            <GIcon name="more" />
          </button>
        </header>
        <div className="lv-stage">
          <div className="lv-pic">
            {wait ? (
              <div className="curtain">
                <div className="curtain-pic" style={session.poster_url ? { backgroundImage: `url("${session.poster_url}")` } : undefined} />
                <p className="eyebrow">{p.clock ? p.goesLiveAt : `${hostFirst} goes live any moment`}</p>
                <div className="count" aria-live="polite">
                  {p.clock ?? "00:00"}
                </div>
                {p.agenda.length ? (
                  <ol className="tonight">
                    {p.agenda.map((t, i) => (
                      <li key={t}>
                        <span>{i + 1}</span>
                        {t}
                      </li>
                    ))}
                  </ol>
                ) : null}
              </div>
            ) : (
              <>
                {showTile("maya") ? (
                  <div className={tileClass("maya")} data-tile="maya">
                    {hostPicture}
                    <span className="cam-n">
                      {host && state.speakingId === host.id ? bars : null}
                      {hostName} <em>Host</em>
                    </span>
                  </div>
                ) : null}
                {showTile("you") && me ? (
                  <div className={tileClass("you")} data-tile="you">
                    <TrackVideo track={meeting.trackOf(me.id, "video")} mirror mine label="You, on stage" />
                    <span className="cam-n">
                      <GIcon name={state.mine.micOn ? "mic" : "micoff"} size={14} /> {state.inSmallRoom ? "You" : "You, on stage"}
                      {state.mine.micOn ? bars : null}
                    </span>
                  </div>
                ) : null}
                {showTile("spot") && spotFace ? (
                  <div className={tileClass("spot")} data-tile="spot">
                    {spotFace.cameraOn ? <TrackVideo track={meeting.trackOf(spotFace.id, "video")} label={spotFace.name} meeting={meeting} peer={spotFace.id} /> : <span className="ini big-ini">{initialsOf(spotFace.name)}</span>}
                    <span className="cam-n">
                      <GIcon name={spotFace.micOn ? "mic" : "micoff"} size={14} /> {spotFace.name}
                    </span>
                  </div>
                ) : null}
                {showTile("partner") && partner ? (
                  <div className={tileClass("partner")} data-tile="partner">
                    {partner.cameraOn ? <TrackVideo track={meeting.trackOf(partner.id, "video")} label={partner.name} meeting={meeting} peer={partner.id} /> : <span className="ini big-ini">{initialsOf(partner.name)}</span>}
                    <span className="cam-n">
                      {state.speakingId === partner.id ? bars : null}
                      <bdi>{partner.name}</bdi>
                    </span>
                  </div>
                ) : null}
                {bigT === "share" && sharer ? (
                  <div className="share big" data-tile="share">
                    <span className="share-tag">
                      <GIcon name="share" size={15} /> <bdi>{firstOf(sharer.name)}</bdi>’s screen
                    </span>
                    <TrackVideo track={meeting.trackOf(sharer.id, "screen")} contain label={`${sharer.name}’s screen`} />
                  </div>
                ) : null}
                {bigT === "myshare" ? (
                  <div className="share myshare big" data-tile="myshare">
                    <span className="share-tag">
                      <GIcon name="share" size={15} /> Your screen
                    </span>
                    <TrackVideo track={meeting.trackOf(state.mine.id, "screen")} contain label="Your screen" />
                  </div>
                ) : null}
                <div
                  className={fit && !fit.hostBig ? "grid-all half" : "grid-all"}
                  ref={grid}
                  style={everyone && fit ? gridStyle(fit) : undefined}
                >
                  {everyone ? (
                    <>
                      <div className={fit?.hostBig === false ? "seat host" : "seat host big"} style={short?.index === 0 ? { gridColumnStart: short.column } : undefined}>
                        {hostPicture}
                        <span className="seat-n" style={{ display: "block" }}>
                          {hostName} · Host
                        </span>
                      </div>
                      {onGrid.map((f, i) => (
                        <Seat
                          key={f.id}
                          column={short?.index === i + 1 ? short.column : undefined}
                          name={f.name}
                          you={f.mine}
                          meeting={meeting}
                          peer={f.id}
                          track={f.mine ? youTrack : undefined}
                          cameraOn={f.cameraOn}
                          micOn={f.micOn}
                          speaking={state.speakingId === f.id}
                          place={placeInLine(state.faces, f.who)}
                          reconnecting={reconnecting}
                          mirror={f.mine}
                          onExpand={f.mine ? undefined : bringForward}
                        />
                      ))}
                    </>
                  ) : null}
                </div>
                <div className="stage-ctl">{stageCtl}</div>
                {p.ended ? (
                  <div className="lv-end">
                    <div className="lv-end-c">
                      <p className="eyebrow"><bdi>{hostFirst}</bdi> ended the live</p>
                      <h2>Thank you for coming, <bdi>{p.viewer.first}</bdi>.</h2>
                      <button className="btn btn-l btn-m" type="button" onClick={p.onAfter}>
                        Stay for the replay
                      </button>
                    </div>
                  </div>
                ) : null}
                {offerFolded && p.offer ? (
                  <div className="lv-offerpill">
                    {hostPhoto ? <img className="av" src={hostPhoto} alt="" /> : null}
                    <OfferPress offer={p.offer.offer} host={hostFirst} viewerEmail={p.offer.email} className="go" onConvert={p.offer.onConvert} onChat={() => openCol("maya")}>
                      {offerPill(p.offer.offer, hostFirst)} <GIcon name="arrowr" size={16} />
                    </OfferPress>
                  </div>
                ) : null}
              </>
            )}
          </div>
          <Floats lane={p.reacts.lane} cap={phone ? 8 : 10} />
          <div className="lower">{lowerCard}</div>
          {myPlace && !lowerCard ? (
            <div className="hand-pill" role="status">
              <GIcon name="hand" size={16} />
              <span>Hand up, number {myPlace}</span>
              <button className="ibtn" type="button" aria-label="Lower your hand" onClick={raise}>
                <GIcon name="close" size={16} />
              </button>
            </div>
          ) : null}
        </div>
        {hk ? (
          <HostAsks asks={asks} chime={playChime} onAnswer={hk.onAnswer} onCall={(id) => void meeting.callOn(id)} onSeeAll={seeAsks} />
        ) : null}
        {arrived && wait ? (
          <p className="arrivals">
            {arrived.photo ? <img className="av" src={arrived.photo} alt="" /> : <span className="av av-i">{initialsOf(arrived.name)}</span>} {firstOf(arrived.name)} just came in
          </p>
        ) : null}
        <div className="lv-dock" ref={dock}>
          {wait ? null : (
            <div className="db-l">
              <DockButton
                icon={everyone ? "stage" : "grid"}
                words={everyone ? (state.sharingId ? "Shared screen" : "Speaker") : "Everyone"}
                className="wide"
                onClick={() => {
                  setView(everyone ? "stage" : "everyone");
                  setPage(0);
                  setSpot(null);
                }}
              />
              <DockButton icon="search" words="Find" className="wide find" onClick={() => openCol("people")} />
            </div>
          )}
          <div className="db-g" ref={dockGroup}>
            <span className="db-w">
              <DockButton icon={state.mine.micOn ? "mic" : "micoff"} words={state.mine.micOn ? "Mute" : "Unmute"} className={state.mine.micOn ? undefined : "off"} onClick={() => {
                void meeting.toggleMic();
                if (!wait && !state.mine.micOn && !onstage) say(`Your mic is on. ${hostFirst} and the room can hear you.`, "mic");
              }} />
              {wait ? null : (
                <button className="db-caret" type="button" data-menu="" aria-label="Microphone and speaker" ref={(el) => { anchors.current.mic = el; }} onClick={() => setMenu((m) => (m === "mic" ? null : "mic"))}>
                  <GIcon name="chu" size={14} stroke={2.4} />
                </button>
              )}
            </span>
            <span className="db-w">
              <DockButton icon={state.mine.cameraOn ? "cam" : "camoff"} words="Camera" className={state.mine.cameraOn ? undefined : "off"} onClick={() => void meeting.toggleCamera()} />
              {wait ? null : (
                <button className="db-caret" type="button" data-menu="" aria-label="Camera settings" ref={(el) => { anchors.current.cam = el; }} onClick={() => setMenu((m) => (m === "cam" ? null : "cam"))}>
                  <GIcon name="chu" size={14} stroke={2.4} />
                </button>
              )}
            </span>
            {reactsFit ? (
              <QuickReactions onTap={p.reacts.tap} off={reactsOff} />
            ) : (
              <button
                className="db react"
                type="button"
                data-menu=""
                ref={(el) => { anchors.current.react = el; }}
                aria-haspopup="dialog"
                aria-expanded={menu === "react"}
                aria-disabled={reactsOff || undefined}
                onClick={() => {
                  if (!reactsOff) setMenu((m) => (m === "react" ? null : "react"));
                }}
              >
                <GIcon name="smile" size={22} />
                <span className="db-t">React</span>
              </button>
            )}
            {wait ? null : (
              <>
                <button
                  className={["db wide", share.rule === "host" && !sharing ? "locked" : "", sharing ? "sharing-on" : ""].filter(Boolean).join(" ")}
                  type="button"
                  data-menu=""
                  ref={(el) => { anchors.current.share = el; }}
                  aria-label={sharing ? "Stop sharing your screen" : hk ? "Share your screen" : share.rule === "host" ? `Only ${hostFirst} shares in this room` : SHARE_WORDS[share.rule]}
                  onClick={askShare}
                >
                  <GIcon name={!hk && !sharing && share.rule === "host" ? "lock" : "share"} size={22} />
                  <span className="db-t">{sharing ? "Stop sharing" : hk ? "Share" : share.rule === "host" ? `Only ${hostFirst}` : SHARE_WORDS[share.rule]}</span>
                </button>
                {hk ? (
                  <DockButton icon="hand" words="Hands" className={handsLit ? "lit" : undefined} label={askCount ? `Hands and requests, ${askCount} waiting` : "Hands and requests"} onClick={seeAsks}>
                    {askCount > 0 ? <span className="cnt">{askCount > 99 ? "99+" : askCount}</span> : null}
                  </DockButton>
                ) : (
                  <DockButton icon="hand" words={myPlace ? "Hand up" : phone ? "Hand" : "Raise hand"} className={myPlace ? "up" : undefined} label={myPlace ? `Hand up, number ${myPlace}. Lower it` : "Raise your hand"} onClick={raise} />
                )}
                {hk ? <DockButton icon="list" words="Host tools" className="wide" onClick={() => setTools("tools")} /> : null}
              </>
            )}
            <button className="db chatb" type="button" onClick={() => openCol()}>
              <GIcon name="chat" size={22} />
              <span className="db-t">Chat</span>
              {unreadRoom > 0 ? <span className="cnt">{unreadRoom > 99 ? "99+" : unreadRoom}</span> : null}
            </button>
            {state.inSmallRoom ? (
              <DockButton icon="people" words="Back to the main room" className="backmain" onClick={() => void meeting.backToMainRoom()} />
            ) : null}
            {wait ? null : <DockButton icon="more" words="More" className="wide" onClick={() => setMore(true)} />}
          </div>
          {hk ? <DockButton icon="leave" words="End the live" className="leave" onClick={hk.onEnd} /> : <DockButton icon="leave" words="Leave" className="leave" onClick={p.onLeave} />}
        </div>
        <div className="lv-row" ref={rowBox}>
          {state.inSmallRoom ? (
            <p className="row-note">
              <GIcon name="people" size={16} /> In a small room. Everyone else is in the main room.
            </p>
          ) : (
            <>
              {onstage || hk ? null : (
                <Seat
                  name={me?.name ?? p.viewer.first}
                  you
                  photo={p.viewer.avatar}
                  track={youTrack}
                  cameraOn={state.mine.cameraOn}
                  micOn={state.mine.micOn}
                  speaking={!!me && state.speakingId === me.id}
                  place={myPlace}
                  reconnecting={reconnecting}
                  mirror
                  onExpand={sayYou}
                />
              )}
              {bigRoom && !wait ? (
                <div className="crowd">
                  {others.map((f) => (
                    <Seat key={f.id} name={f.name} meeting={meeting} peer={f.id} cameraOn={f.cameraOn} micOn={f.micOn} speaking={state.speakingId === f.id} place={placeInLine(state.faces, f.who)} onExpand={spotInRow} />
                  ))}
                </div>
              ) : wait ? (
                waiting.map((f) => (
                  <Seat key={f.key} name={f.name} photo={f.photo} track={null} cameraOn={false} micOn={false} arriving={arrived?.key === f.key} place={0} />
                ))
              ) : (
                others.map((f) => (
                  <Seat key={f.id} name={f.name} meeting={meeting} peer={f.id} cameraOn={f.cameraOn} micOn={f.micOn} speaking={state.speakingId === f.id} place={placeInLine(state.faces, f.who)} onExpand={bringForward} />
                ))
              )}
              {moreN > 0 ? (
                <button className="seat more" type="button" onClick={() => openCol("people")}>
                  {unseated.length > 0 ? (
                    <span className="stack">
                      {unseated.map((f) => (
                        <img key={f.key} src={f.photo} alt="" />
                      ))}
                    </span>
                  ) : null}
                  +{fmt(moreN)}
                  <span className="mw">&nbsp;more</span>
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>
      <aside className="lv-col" aria-label="The room">
        <LiveColumn
          tab={tab}
          onTab={(t) => {
            setTab(t);
            if (t === "maya") p.onReadHost();
          }}
          stage={wait ? "wait" : "live"}
          wide={wide}
          onTuck={tuckCol}
          session={session}
          hostFirst={hostFirst}
          viewer={p.viewer}
          chat={chat}
          unreadRoom={unreadRoom}
          unreadHost={p.unreadHost}
          opensAt={p.opensAt}
          liveAt={wait ? null : p.liveAt}
          ended={p.ended}
          reconnecting={reconnecting}
          send={p.send}
          faces={state.faces}
          speakingId={state.speakingId}
          myPlace={myPlace}
          onHand={raise}
          thread={p.thread}
          find={find}
          onFind={setFind}
          host={hk ? { kit: hk, meeting, tools: state.tools, waiting: askCount, lit: handsLit } : undefined}
        />
      </aside>
      {phone && colOpen ? <div className="scrim on" role="presentation" onClick={tuckCol} /> : null}
      <button className="lv-colpill" type="button" onClick={() => openCol("room")}>
        <GIcon name="untuck" size={18} /> Chat {unreadRoom > 0 ? <em>{unreadRoom > 99 ? "99+" : unreadRoom}</em> : null}
      </button>
      {peek ? (
        <div className="col-peek on">
          {peekPhoto ? (
            <img className="av" src={peekPhoto} alt="" />
          ) : (
            <span className="av av-i">{initialsOf(peek.name)}</span>
          )}
          <span>
            <b><bdi>{firstOf(peek.name)}</bdi></b> <bdi dir="auto">{peek.body}</bdi>
          </span>
        </div>
      ) : null}
      {reconnecting ? (
        <div className="lv-banner" role="status">
          <span className="spin" />
          Reconnecting to the live… The room stays right here.
        </div>
      ) : chat.hostAway ? (
        <div className="lv-banner" role="status">
          <GIcon name="clock" size={18} /> <bdi>{hostFirst}</bdi> stepped out. Stay here, they may be back in a moment.
        </div>
      ) : back ? (
        <div className="lv-banner" role="status">
          <GIcon name="check" size={18} /> You’re back.
        </div>
      ) : banner ? (
        <div className="lv-banner" role="status">
          {banner}
        </div>
      ) : null}
      {state.notice ? (
        <div className="lv-banner" role="status">
          {state.notice}
        </div>
      ) : null}
      {menu === "mic" ? (
        <DockMenu anchor={anchors.current.mic} room={room.current} label="Microphone and speaker">
          <DeviceSelect label="Microphone" choices={state.microphones} chosen={state.micId} onPick={(id) => void meeting.chooseMic(id)} />
          {canChooseSpeaker() ? <DeviceSelect label="Speaker" choices={state.speakers} chosen={state.speakerId} onPick={(id) => meeting.chooseSpeaker(id)} /> : null}
          {state.mine.canClean ? (
            <label className="tg">
              <input type="checkbox" checked={state.mine.cleaning} onChange={() => void meeting.toggleClean()} />
              Reduce background noise
            </label>
          ) : null}
        </DockMenu>
      ) : null}
      {menu === "cam" ? (
        <DockMenu anchor={anchors.current.cam} room={room.current} label="Camera">
          <DeviceSelect label="Camera" choices={namedCameras(state.cameras)} chosen={state.cameraId} onPick={(id) => void meeting.chooseCamera(id)} />
          {state.mine.canSoften ? (
            <label className="tg">
              <input type="checkbox" checked={state.mine.softened} onChange={() => void meeting.toggleSoften()} />
              Soften what is behind you
            </label>
          ) : null}
        </DockMenu>
      ) : null}
      {menu === "react" ? (
        <DockMenu anchor={anchors.current.react} room={room.current} label="Reactions" className="qpill">
          <QuickReactions onTap={p.reacts.tap} />
        </DockMenu>
      ) : null}
      {menu === "share" ? (
        <DockMenu anchor={anchors.current.share} room={room.current} label="Share your screen" className="sharecard">
          <ShareCardBody
            rule={share.rule}
            stand={share.mine}
            asked={asked}
            hostFirst={hostFirst}
            onSend={() => {
              setAsked(true);
              p.send({ type: "share-ask" });
            }}
            onCancel={() => {
              if (asked) p.send({ type: "share-drop" });
              setAsked(false);
              setMenu(null);
            }}
            onPick={(what) => {
              if (share.rule === "anyone" && !allowed) {
                pendingPick.current = what;
                setAsked(true);
                p.send({ type: "share-ask" });
                return;
              }
              startShare(what);
            }}
            onClose={() => {
              if (share.mine === "declined" || share.mine === "busy") setAsked(false);
              setMenu(null);
            }}
          />
        </DockMenu>
      ) : null}
      {more ? (
        <Sheet label="More" onClose={() => setMore(false)}>
          <div className="sx-in">
            <h3 className="sx-h">More</h3>
            {state.inSmallRoom ? (
              <div className="sx-sec">
                <button className="btn btn-p btn-w" type="button" onClick={() => {
                  setMore(false);
                  void meeting.backToMainRoom();
                }}>
                  <GIcon name="people" size={18} /> Back to the main room
                </button>
              </div>
            ) : null}
            {wait ? null : (
              <div className="sx-sec">
                <p className="sx-l">View</p>
                <div className="sx-seg">
                  <button type="button" className={everyone ? undefined : "on"} onClick={() => setView("stage")}>
                    <GIcon name="stage" size={18} /> {state.sharingId ? "Shared screen" : `${hostFirst} on stage`}
                  </button>
                  <button type="button" className={everyone ? "on" : undefined} onClick={() => {
                    setView("everyone");
                    setPage(0);
                    setSpot(null);
                  }}>
                    <GIcon name="grid" size={18} /> Everyone
                  </button>
                </div>
              </div>
            )}
            <div className="sx-sec">
              <button className="sx-row" type="button" onClick={() => {
                setMore(false);
                openCol("people");
              }}>
                <GIcon name="search" size={20} /> Find someone
              </button>
              {hk ? (
                <button className="sx-row" type="button" onClick={() => {
                  setMore(false);
                  setTools("tools");
                }}>
                  <GIcon name="list" size={20} /> Host tools
                  <span>Who may share, mute everyone, small rooms, recording, the room link</span>
                </button>
              ) : null}
              {wait || hk ? null : (
                <button className="sx-row" type="button" onClick={() => {
                  if (cannotShare) {
                    say("Join from a computer to share it.", "share");
                    return;
                  }
                  setMore(false);
                  window.setTimeout(askShare, 420);
                }}>
                  <GIcon name={share.rule === "host" ? "lock" : "share"} size={20} />{" "}
                  {sharing ? "Stop sharing" : share.rule === "ask" ? "Ask to share your screen" : "Share your screen"}
                  <span>
                    {cannotShare
                      ? "Join from a computer to share it."
                      : share.rule === "host"
                        ? `Only ${hostFirst} shares in this room`
                        : share.rule === "ask"
                          ? `${hostFirst} decides who shares`
                          : "Anyone can share in this room"}
                  </span>
                </button>
              )}
            </div>
            <div className="sx-sec">
              <p className="sx-l">Your devices</p>
              <div className="dev" style={{ marginTop: 0 }}>
                <DeviceSelect label="Camera" choices={namedCameras(state.cameras)} chosen={state.cameraId} onPick={(id) => void meeting.chooseCamera(id)} />
                <DeviceSelect label="Microphone" choices={state.microphones} chosen={state.micId} onPick={(id) => void meeting.chooseMic(id)} />
                {canChooseSpeaker() ? <DeviceSelect label="Speaker" choices={state.speakers} chosen={state.speakerId} onPick={(id) => meeting.chooseSpeaker(id)} /> : null}
              </div>
              <div style={{ display: "grid", gap: 14, marginTop: 16 }}>
                {state.mine.canClean ? (
                  <label className="tg">
                    <input type="checkbox" checked={state.mine.cleaning} onChange={() => void meeting.toggleClean()} />
                    Reduce background noise
                  </label>
                ) : null}
                {state.mine.canSoften ? (
                  <label className="tg">
                    <input type="checkbox" checked={state.mine.softened} onChange={() => void meeting.toggleSoften()} />
                    Soften what is behind you
                  </label>
                ) : null}
              </div>
            </div>
            <button className="btn btn-g btn-w" type="button" style={{ marginTop: 6 }} onClick={() => setMore(false)}>
              Done
            </button>
          </div>
        </Sheet>
      ) : null}
      {hk && tools === "tools" ? (
        <HostTools kit={hk} meeting={meeting} state={state} rule={chat.share.rule} hasHands={raised.length > 0} onRooms={() => setTools("rooms")} onClose={() => setTools(null)} />
      ) : null}
      {hk && tools === "rooms" ? <HostSmallRooms meeting={meeting} state={state} onClose={() => setTools(null)} /> : null}
      <Toast toast={toast} />
    </div>
  );
}

/** The share card, as the host's rule and the room's answer have it. */
function ShareCardBody({
  rule,
  stand,
  asked,
  hostFirst,
  onSend,
  onCancel,
  onPick,
  onClose,
}: {
  rule: ShareRule;
  stand: string;
  asked: boolean;
  hostFirst: string;
  onSend: () => void;
  onCancel: () => void;
  onPick: (what: string) => void;
  onClose: () => void;
}): ReactNode {
  const pick = (askedFirst: boolean) => (
    <>
      <p className="sc-t">
        <GIcon name="check" size={18} /> {askedFirst ? `${hostFirst} let you share. Pick what to share` : "Pick what to share"}
      </p>
      <div className="pick">
        {PICKS.map(([words, icon]) => (
          <button key={words} type="button" onClick={() => onPick(words)}>
            <GIcon name={icon} size={18} /> {words}
          </button>
        ))}
      </div>
      <div className="sc-a">
        <button className="btn btn-q" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </>
  );
  if (rule === "host") {
    return (
      <>
        <p className="sc-t">
          <GIcon name="lock" size={18} /> Only <bdi>{hostFirst}</bdi> shares in this room
        </p>
        <p className="sc-p"><bdi>{hostFirst}</bdi> set this room so only they share a screen. You can still raise your hand to speak.</p>
        <div className="sc-a">
          <button className="btn btn-g btn-s" type="button" onClick={onClose}>
            OK
          </button>
        </div>
      </>
    );
  }
  if (asked && stand === "busy") {
    return (
      <>
        <p className="sc-t">
          <GIcon name="clock" size={18} /> Someone is sharing right now.
        </p>
        <p className="sc-p">You can share when they stop.</p>
        <div className="sc-a">
          <button className="btn btn-g btn-s" type="button" onClick={onClose}>
            OK
          </button>
        </div>
      </>
    );
  }
  if (rule === "anyone") return pick(false);
  if (asked && stand === "allowed") return pick(true);
  if (asked && stand === "declined") {
    return (
      <>
        <p className="sc-t">
          <GIcon name="clock" size={18} /> <bdi>{hostFirst}</bdi> said not now.
        </p>
        <p className="sc-p">You can ask again later.</p>
        <div className="sc-a">
          <button className="btn btn-g btn-s" type="button" onClick={onClose}>
            OK
          </button>
        </div>
      </>
    );
  }
  if (asked) {
    return (
      <>
        <p className="sc-t">
          <span className="spin" /> Waiting for <bdi>{hostFirst}</bdi> to say yes
        </p>
        <p className="sc-p">You can close this. The answer shows on the stage.</p>
        <div className="sc-a">
          <button className="btn btn-q" type="button" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </>
    );
  }
  return (
    <>
      <p className="sc-t">Ask <bdi>{hostFirst}</bdi> to share your screen?</p>
      <p className="sc-p">They see your request and decide. Everyone will see what you share.</p>
      <div className="sc-a">
        <button className="btn btn-p btn-s" type="button" onClick={onSend}>
          <GIcon name="send" size={16} /> Send
        </button>
        <button className="btn btn-q" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </>
  );
}

/** Full screen for the whole room, where the browser allows it. */
function FullScreenButton({ room }: { room: RefObject<HTMLDivElement | null> }): ReactNode {
  const [full, setFull] = useState(false);
  useEffect(() => {
    const sync = () => setFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);
  // Why not shown: an iPhone cannot take part of a page full screen, and a button that does nothing is worse than none.
  if (!document.fullscreenEnabled) return null;
  return (
    <button
      className="sc-b sq"
      type="button"
      aria-label="Full screen"
      title="Full screen"
      onClick={() => {
        if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
        else void room.current?.requestFullscreen?.().catch(() => {});
      }}
    >
      <GIcon name={full ? "unfull" : "full"} size={16} />
    </button>
  );
}

